import { apiClient } from '@/lib/api/client';
import type { Product, ImageObject, ServerPriceObject, ProductAssociations, ImageOrderData } from '@/lib/types';
import type { ProductFilters, CreateProductData, UpdateProductData } from '../types/products.types';

interface ApiData<T> {
  data: T;
}

// Server response types
interface ServerPromotion {
  refId?: string;
  name?: string;
  displayName?: string;
  type?: string;
  value?: number;
  status?: string;
  badgeColor?: string;
  startDate?: string;
  endDate?: string;
  // For buy_x_get_y
  buyQuantity?: number;
  getQuantity?: number;
}

interface ServerProduct {
  id?: string;
  refId: string;
  name: string;
  description?: string;
  stock: number;
  status: string;
  images: ImageObject[];
  prices?: ServerPriceObject;
  discount?: number;
  categories?: Array<{ id: number; name: string }>;
  shop?: {
    id: string;
    refId?: string;
    name: string;
    city?: string;
    country?: string;
  };
  shopName?: string;
  shopId?: string;
  gender?: string;
  unit?: string;
  sizes?: string[];
  tags?: string[];
  weight?: number;
  weight_unit?: string;
  promotion?: ServerPromotion;
  reviews?: {
    averageRating: number;
    totalReviews: number;
    ratingDistribution?: Record<number, number>;
  };
  associations?: ProductAssociations;
}

interface ServerProductListItem {
  id?: string;
  refId: string;
  name: string;
  stock: number;
  status: string;
  image?: ImageObject;
  images?: ImageObject[];
  prices?: ServerPriceObject;
  categories?: Array<{ id: number; name: string }>;
  shopName?: string;
  shopId?: string;
  hasPromotion?: boolean;
}

interface ProductListResponse {
  data: ServerProductListItem[];
  total: number;
  page: number;
  lastPage: number;
}

/**
 * Extracts price from server price object
 * Prioritizes comoros (KMF) as a fallback
 */
function extractPrice(prices?: ServerPriceObject): number {
  if (!prices) return 0;
  // Try original first, then comoros (KMF)
  if (prices.original !== undefined) return prices.original;
  const komorosPrice = parseFloat(prices.comoros || '0');
  if (!isNaN(komorosPrice) && komorosPrice > 0) return komorosPrice;
  const euroPrice = parseFloat(prices.euro || '0');
  if (!isNaN(euroPrice) && euroPrice > 0) return euroPrice;
  const usdPrice = parseFloat(prices.usd || '0');
  return isNaN(usdPrice) ? 0 : usdPrice;
}

/**
 * Transforms server product list item to frontend Product
 */
function transformProductListItem(item: ServerProductListItem): Product {
  const images: ImageObject[] = [];
  if (item.image) images.push(item.image);
  if (item.images) images.push(...item.images);

  return {
    id: item.id || item.refId,
    refId: item.refId,
    name: item.name,
    stock: item.stock,
    status: item.status as Product['status'],
    price: extractPrice(item.prices),
    prices: item.prices,
    images: images,
    categories: item.categories,
    categoryName: item.categories?.[0]?.name,
    shopName: item.shopName,
    shopId: item.shopId,
    createdAt: '',
    updatedAt: '',
  };
}

/**
 * Transforms server product to frontend Product
 */
function transformProduct(item: ServerProduct): Product {
  return {
    id: item.id || item.refId,
    refId: item.refId,
    name: item.name,
    description: item.description,
    stock: item.stock,
    status: item.status as Product['status'],
    price: extractPrice(item.prices),
    prices: item.prices,
    discount: item.discount,
    images: item.images || [],
    categories: item.categories,
    categoryName: item.categories?.[0]?.name,
    shop: item.shop ? {
      id: item.shop.id,
      refId: item.shop.refId,
      name: item.shop.name,
      city: item.shop.city,
      country: item.shop.country,
    } : undefined,
    shopName: item.shop?.name || item.shopName,
    shopId: item.shop?.refId || item.shop?.id || item.shopId,
    gender: item.gender,
    unit: item.unit,
    sizes: item.sizes,
    tags: item.tags,
    weight: item.weight,
    weight_unit: item.weight_unit,
    promotion: item.promotion,
    reviews: item.reviews,
    associations: item.associations,
    createdAt: '',
    updatedAt: '',
  };
}

export interface PaginatedProducts {
  products: Product[];
  page: number;
  totalPages: number;
  total: number;
}

export const productsService = {
  async getProducts(filters: ProductFilters = {}): Promise<PaginatedProducts> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (filters.search) params.set('searchTerm', filters.search);
    if (filters.categoryId) params.set('category', filters.categoryId);
    if (filters.shopId) params.set('shopRefIds', filters.shopId);

    const response = await apiClient.get<ApiData<ProductListResponse>>(`/product/all?${params.toString()}`);
    const data = response.data?.data;
    const items = data?.data ?? [];
    return {
      products: items.map(transformProductListItem),
      page: data?.page ?? page,
      totalPages: data?.lastPage ?? 1,
      total: data?.total ?? items.length,
    };
  },

  async getProduct(id: string): Promise<Product | null> {
    const response = await apiClient.get<ApiData<ServerProduct>>(`/product/?id=${id}`);
    const data = response.data?.data;
    if (!data) return null;
    return transformProduct(data);
  },

  async createProduct(data: CreateProductData, images: File[]): Promise<Product> {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('price', String(data.price));
    if (data.discountPrice && data.discountPrice > 0) {
      formData.append('discountPrice', String(data.discountPrice));
    }
    formData.append('quantity', String(data.quantity));
    formData.append('shopId', data.shopId);
    formData.append('currency', data.currency || 'european_currency');
    formData.append('unit', data.unit || 'sold_individually');
    formData.append('gender', data.gender || 'none');
    formData.append('categories', JSON.stringify(data.categories || []));
    formData.append('sizes', JSON.stringify(data.sizes || []));
    formData.append('tags', (data.tags || []).join(','));
    if (data.weight !== undefined && data.weight > 0) formData.append('weight', String(data.weight));
    if (data.weight_unit) formData.append('weight_unit', data.weight_unit);

    images.forEach((image) => {
      formData.append('images', image);
    });

    const response = await apiClient.post<ApiData<Product>>('/product/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data ?? null;
  },

  async updateProduct(productRefId: string, shopRefId: string, data: Partial<UpdateProductData>): Promise<Product | null> {
    const response = await apiClient.put<ApiData<Product>>('/product/update/information', {
      productRefId,
      shopRefId,
      ...data,
    });
    return response.data?.data ?? null;
  },

  async updateProductStatus(id: string, status: string): Promise<Product | null> {
    const response = await apiClient.put<ApiData<Product>>('/product/update/status', {
      id,  // Note: server DTO uses 'id' for status update
      status,
    });
    return response.data?.data ?? null;
  },

  async deleteProduct(id: string, reason?: string): Promise<void> {
    await apiClient.delete('/product/delete', {
      data: { productRefId: id, reason },
    });
  },

  async updateProductImages(id: string, images: File[], orders: ImageOrderData[]): Promise<Product> {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('orders', JSON.stringify(orders));

    // Only append new images (files)
    images.forEach((image) => {
      formData.append('images', image);
    });

    const response = await apiClient.put<ApiData<Product>>('/product/update/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data ?? null;
  },
};
